import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Lock, Church, Crown, ArrowLeft, CheckCircle, Trophy, Play } from 'lucide-react';
import { getGameProgress, createCheckout, getRoundStatus, getLeaderboard } from '../utils/api';
import { toast } from 'sonner';

const levels = [
  { level: 1, name: 'Easy', icon: '📖', color: 'from-green-400 to-green-600', premium: false, questions: 5, image: 'https://customer-assets.emergentagent.com/job_holygame/artifacts/gxx89lfk_Old%20White%20Man.png' },
  { level: 2, name: 'Moderate', icon: '⛪', color: 'from-blue-400 to-blue-600', premium: false, questions: 5, image: 'https://customer-assets.emergentagent.com/job_holygame/artifacts/g8f00cay_Young%20black%20guy.png' },
  { level: 3, name: 'Hard', icon: '✝️', color: 'from-purple-400 to-purple-600', premium: false, questions: 10, image: 'https://customer-assets.emergentagent.com/job_holygame/artifacts/g4vsuyd9_Young%20white%20girl.png' },
  { level: 4, name: 'Minister', icon: '🕊️', color: 'from-yellow-400 to-yellow-600', premium: true, questions: 15, image: 'https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9vz1ncw6_white%20woman.png' },
  { level: 5, name: 'Pastor', icon: '👼', color: 'from-pink-400 to-pink-600', premium: true, questions: 15, image: 'https://customer-assets.emergentagent.com/job_holygame/artifacts/nzi0y6fy_Old%20Black%20Man.png' },
  { level: 6, name: 'Apostle', icon: '🌟', color: 'from-red-400 to-red-600', premium: true, questions: 20, image: 'https://customer-assets.emergentagent.com/job_holygame/artifacts/v7d1zoji_Black%20Girl.png' },
];

export default function LevelSelect({ user, refreshUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [userRank, setUserRank] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [lockedLevels, setLockedLevels] = useState([]);
  const [currentRound, setCurrentRound] = useState(1);
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [roundModalType, setRoundModalType] = useState(''); // 'round1', 'round2-win', 'round2-lose'
  const [currentMemberCount, setCurrentMemberCount] = useState(0); // Store actual member count for modals

  // Refresh user data whenever we navigate to this page
  useEffect(() => {
    const guestMode = localStorage.getItem('guest_mode') === 'true';
    setIsGuestMode(guestMode && !user);
    
    const initializeLevelSelect = async () => {
      if (user && refreshUser) {
        // Force refresh user data to get latest total_members from backend
        await refreshUser();
      }
      if (user) {
        loadProgress();
        loadUserRanking();
        loadRoundStatus();
      }
    };

    initializeLevelSelect();
  }, [location.pathname, user]); // Trigger when location changes or user updates

  // Separate useEffect to check instructions on mount - only once per session
  useEffect(() => {
    const hasSeenInstructions = localStorage.getItem('has_seen_instructions');
    const hasSeenThisSession = sessionStorage.getItem('has_seen_instructions_this_session');
    
    // Only show if never seen before AND not shown in this session
    if (!hasSeenInstructions && !hasSeenThisSession) {
      // Mark as seen in session storage to prevent showing again during this session
      sessionStorage.setItem('has_seen_instructions_this_session', 'true');
      // Add a small delay to ensure proper rendering
      setTimeout(() => {
        setShowInstructions(true);
      }, 500);
    }
  }, []);

  const loadProgress = async () => {
    try {
      const response = await getGameProgress();
      setProgress(response.data.progress || []);
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
  };

  const loadUserRanking = async () => {
    try {
      const response = await getLeaderboard();
      const leaderboard = response.data.leaderboard || [];
      setTotalPlayers(leaderboard.length);
      
      // Find user's rank
      const userIndex = leaderboard.findIndex(player => player.email === user.email);
      if (userIndex !== -1) {
        setUserRank(userIndex + 1);
      }
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  const loadRoundStatus = async () => {
    // Skip round status for guest users
    if (!user || isGuestMode) {
      return;
    }
    
    try {
      const response = await getRoundStatus();
      const { locked_levels, current_round, levels_1_3_locked, current_run_members } = response.data;
      
      setLockedLevels(locked_levels);
      setCurrentRound(current_round);
      setCurrentMemberCount(current_run_members); // Store the actual member count
      
      // Check if we need to show round completion modal
      if (levels_1_3_locked) {
        if (current_round === 1) {
          setRoundModalType('round1');
          setShowRoundModal(true);
        } else if (current_round === 2) {
          if (current_run_members >= 5000) {
            setRoundModalType('round2-win');
          } else {
            setRoundModalType('round2-lose');
          }
          setShowRoundModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to load round status:', error);
    }
  };

  const isLevelUnlocked = (level) => {
    // Level is unlocked if it's not in the locked levels array
    return !lockedLevels.includes(level);
  };

  const isLevelLocked = (level) => {
    // Guest mode: Only Level 1 is unlocked, all others are locked
    if (isGuestMode && level > 1) {
      return true;
    }
    
    // Pro Levels (4-6) require premium purchase
    if (level >= 4 && level <= 6) {
      return !user?.is_premium;
    }
    
    return lockedLevels.includes(level);
  };

  const getLevelProgress = (level) => {
    return progress.find(p => p.level === level);
  };

  const handleLevelClick = async (levelData) => {
    // Check if level is locked
    if (isLevelLocked(levelData.level)) {
      toast.error('This level is locked! You have reached the member threshold.');
      return;
    }

    // TEMPORARY: Premium check disabled for testing levels 4-6
    // if (levelData.premium && (!user || !user.is_premium)) {
    //   setShowPurchaseModal(true);
    //   return;
    // }

    navigate(`/gameplay/${levelData.level}`);
  };

  const handleContinueToGame = () => {
    localStorage.setItem('has_seen_instructions', 'true');
    setShowInstructions(false);
  };

  const handlePremiumUpgrade = async () => {
    if (!user) {
      toast.error('Please login first to purchase');
      navigate('/auth');
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const originUrl = window.location.origin;
      const response = await createCheckout(originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoading(false);
    }
  };

  const handleStartRound2 = async () => {
    try {
      const { startRound2 } = await import('../utils/api');
      await startRound2();
      setShowRoundModal(false);
      // Reload round status to get updated member count
      await loadRoundStatus();
      toast.success('Round 2 started! Play levels 1-3 again.');
    } catch (error) {
      console.error('Failed to start round 2:', error);
      toast.error('Failed to start Round 2');
    }
  };

  const handleResetGame = async () => {
    try {
      const { resetGameNew } = await import('../utils/api');
      await resetGameNew();
      setShowRoundModal(false);
      // Reset local member count
      setCurrentMemberCount(0);
      // Refresh user data and round status
      if (refreshUser) {
        await refreshUser();
      }
      await loadRoundStatus();
      toast.success('Game reset! Start fresh from the beginning.');
    } catch (error) {
      console.error('Failed to reset game:', error);
      toast.error('Failed to reset game');
    }
  };

  return (
    <div 
      className="min-h-screen py-12 px-6 relative"
      data-testid="level-select-page"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      <div className="max-w-6xl mx-auto relative z-10">
        <Button
          data-testid="back-home-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-white hover:bg-white/20"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Button>

        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9qio0cok_Mega%20Church%20Logo%20T%20Shirt.png"
              alt="Mega Church Logo"
              className="w-28 h-28 sm:w-32 sm:h-32 object-contain"
            />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4" data-testid="page-title">Select Your Level</h1>
          
          {isGuestMode && (
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mb-4">
              <span className="text-white font-semibold">👤 Guest Mode</span>
            </div>
          )}
          
          {user ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-xl text-white/90">
                Welcome, <span className="font-bold">{user.name}</span>
              </p>
              <p className="text-lg text-white/90">
                Total Members: <span className="font-bold text-2xl">{user.total_members}</span>
              </p>
              {userRank && (
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mt-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-bold">
                    Leaderboard Rank: #{userRank} of {totalPlayers}
                  </span>
                </div>
              )}
              {user.is_premium && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 px-4 py-2 rounded-full">
                  <Crown className="w-5 h-5 text-white" />
                  <span className="text-white font-bold">Premium Member</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-white/80 text-sm italic">Login to save progress and compete on leaderboard</p>
          )}
        </div>

        {/* Game Instructions Banner */}
        <div className="mb-8 px-4">
          <div className="relative bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] rounded-3xl p-1 shadow-2xl">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] rounded-3xl blur-lg opacity-60 animate-pulse"></div>
            
            {/* Content */}
            <div className="relative bg-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10">
              <div className="text-center">
                <div className="mb-4">
                  <span className="text-5xl sm:text-6xl">🏆</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold bg-gradient-to-r from-[#4169E1] via-[#9370DB] to-[#FFD700] bg-clip-text text-transparent mb-4 leading-tight">
                  Take your time to play through the levels.
                </h2>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 leading-relaxed">
                  All levels must be completed with{' '}
                  <span className="text-white bg-gradient-to-r from-yellow-400 to-yellow-600 px-3 py-1 rounded-full inline-block transform hover:scale-105 transition-all">
                    100%
                  </span>
                  {' '}to win and reach{' '}
                  <span className="text-[#4169E1] font-extrabold uppercase tracking-wide">
                    MEGA CHURCH!
                  </span>
                </p>
                <div className="mt-4 flex justify-center gap-2">
                  <span className="text-3xl animate-bounce">⭐</span>
                  <span className="text-3xl animate-bounce delay-100">⭐</span>
                  <span className="text-3xl animate-bounce delay-200">⭐</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {levels.map((levelData, index) => {
            const levelProgress = getLevelProgress(levelData.level);
            const unlocked = isLevelUnlocked(levelData.level);
            const locked = isLevelLocked(levelData.level);
            const needsPremium = levelData.premium && (!user || !user.is_premium);
            const completedQuestions = levelProgress?.score ? Math.floor(levelProgress.score / (levelData.level <= 3 ? 10 : 40)) : 0;

            return (
              <React.Fragment key={levelData.level}>
                <Card
                  data-testid={`level-${levelData.level}-card`}
                  className="relative overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300 bg-white/95 backdrop-blur-lg shadow-xl"
                  onClick={() => handleLevelClick(levelData)}
                >
                {/* Colored Header Bar */}
                <div className={`h-2 sm:h-3 bg-gradient-to-r ${levelData.color}`}></div>

                {/* Main Content */}
                <div className="p-4 sm:p-6">
                  {/* Character Image */}
                  <div className="flex justify-center mb-3 sm:mb-4">
                    <img 
                      src={levelData.image}
                      alt={`Level ${levelData.level} - ${levelData.name}`}
                      className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 object-contain"
                    />
                  </div>

                  {/* Level Info */}
                  <div className="text-center mb-3 sm:mb-4">
                    <div className="text-xs sm:text-sm font-bold text-gray-500 mb-1">LEVEL {levelData.level}</div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">{levelData.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{levelData.questions} Questions</p>
                  </div>

                  {/* Stats - Total Members Gained */}
                  {user && levelProgress && (
                    <div className="mb-4">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-600 mb-1">Total Members Gained</div>
                        <div className="text-xl font-bold text-blue-600">+{levelProgress.members_gained}</div>
                      </div>
                    </div>
                  )}

                  {/* Status Badge / Button */}
                  <div className="flex justify-center">
                    {locked ? (
                      <div 
                        className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 px-5 py-2.5 rounded-full shadow-lg cursor-pointer hover:from-red-600 hover:to-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (levelData.level >= 4 && !user?.is_premium) {
                            setShowPurchaseModal(true);
                          }
                        }}
                      >
                        <Lock className="w-4 h-4 text-white" />
                        <span className="font-bold text-white">
                          {isGuestMode && levelData.level > 1 
                            ? 'Sign Up to Unlock' 
                            : levelData.level >= 4 && !user?.is_premium
                            ? 'Purchase Pro - $3'
                            : 'Level Locked'}
                        </span>
                      </div>
                    ) : levelProgress && levelProgress.attempts > 0 ? (
                      <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 px-5 py-2.5 rounded-full shadow-lg">
                        <Play className="w-4 h-4 text-white" />
                        <span className="font-bold text-white">Keep Playing</span>
                      </div>
                    ) : (
                      <div className={`flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r ${levelData.color} rounded-full shadow-lg`}>
                        <Play className="w-4 h-4 text-white" />
                        <span className="font-bold text-white">
                          {levelData.premium ? 'Pro Level - Start' : 'Play'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Attempts Counter - Show unlimited for unlocked levels */}
                  {user && levelProgress && levelProgress.attempts > 0 && !locked && (
                    <div className="mt-3 text-center text-xs text-gray-500">
                      Attempts: {levelProgress.attempts} • Unlimited
                    </div>
                  )}
                </div>
              </Card>

              {/* Premium Unlock Promo Card - After Level 3 */}
              {levelData.level === 3 && (!user || !user.is_premium) && (
                <Card
                  data-testid="premium-unlock-promo"
                  className="relative overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 shadow-2xl border-4 border-yellow-300"
                  onClick={() => setShowPurchaseModal(true)}
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>

                  {/* Gold accent bar */}
                  <div className="h-3 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300"></div>

                  <div className="p-6 relative z-10">
                    {/* Crown Icon */}
                    <div className="flex justify-center mb-4">
                      <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg border-4 border-white/40 animate-pulse">
                        <Crown className="w-12 h-12 text-white" />
                      </div>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-4">
                      <div className="text-sm font-bold text-yellow-900 mb-1">UNLOCK PREMIUM</div>
                      <h3 className="text-2xl font-bold text-white mb-2">Mega Church Pro</h3>
                      <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full">
                        <span className="text-white font-bold">Only $3</span>
                      </div>
                    </div>

                    {/* Benefits List */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                          <span className="text-white font-bold">3X</span>
                        </div>
                        <div className="text-white text-sm font-semibold">
                          Earn 3x Members per Answer
                        </div>
                      </div>

                      <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-white text-sm">
                          3 Advanced Levels
                        </div>
                      </div>

                      <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-white text-sm">
                          50 Challenging Questions
                        </div>
                      </div>

                      <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-white text-sm">
                          Minister → Pastor → Apostle
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="flex justify-center">
                      <div className="bg-white text-yellow-600 px-6 py-3 rounded-full shadow-2xl font-bold text-lg hover:bg-yellow-50 transition-all">
                        Unlock Now - $3
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </React.Fragment>
            );
          })}
        </div>

        {/* How to Play Instructions Modal */}
        <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
          <DialogContent className="max-w-[95vw] sm:max-w-3xl lg:max-w-4xl bg-white p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
            <DialogHeader className="sr-only">
              <DialogTitle>How to Play Instructions</DialogTitle>
              <DialogDescription>Learn how to play the Mega Church Game</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col">
              {/* Instructions Image */}
              <img 
                src="https://customer-assets.emergentagent.com/job_holygame/artifacts/r9cz5wbk_Mega%20Church%20How%20to%20Play%20Instructions%202025.png"
                alt="How to Play Instructions"
                className="w-full h-auto object-contain"
              />
              
              {/* Continue Button Below Image */}
              <div className="p-4 sm:p-6 bg-white">
                <Button
                  onClick={handleContinueToGame}
                  className="w-full h-12 sm:h-16 text-lg sm:text-2xl font-bold bg-gradient-to-r from-[#4169E1] to-[#5B7FE8] text-white hover:from-[#3558c7] hover:to-[#4169E1] rounded-full shadow-lg transform hover:scale-105 transition-all"
                >
                  Continue to Game
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Premium Purchase Modal */}
        <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md bg-white p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl sm:text-3xl text-[#FFD700]">
                <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-[#FFD700]" />
                Unlock Mega Church Pro
              </DialogTitle>
              <DialogDescription className="text-base sm:text-lg pt-2 sm:pt-4 text-gray-700">
                Get access to advanced levels and become a spiritual leader!
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 sm:py-6">
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3 text-gray-800">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold">Level 4 - Minister (15 challenging questions)</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-gray-800">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold">Level 5 - Pastor (15 expert questions)</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-gray-800">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold">Level 6 - Apostle (20 scholar questions)</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-gray-800">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base font-semibold">Earn 40 members per correct answer</span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 p-4 sm:p-6 rounded-xl text-center">
                <p className="text-white text-4xl sm:text-5xl font-bold mb-1 sm:mb-2">$3</p>
                <p className="text-white/90 text-xs sm:text-sm font-semibold">One-time payment</p>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPurchaseModal(false)}
                className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handlePremiumUpgrade}
                disabled={loading}
                className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white font-bold"
              >
                {loading ? 'Processing...' : 'Unlock Now - $3'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Round Completion Modals - Optimized for Mobile, Tablet, Desktop */}
        <Dialog open={showRoundModal} onOpenChange={() => {}}>
          <DialogContent className="max-w-[90vw] sm:max-w-md md:max-w-lg lg:max-w-xl bg-white p-4 sm:p-6 md:p-8 mx-4">
            {roundModalType === 'round1' && (
              <>
                <DialogHeader className="text-center">
                  <DialogTitle className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#4169E1] mb-2">
                    You Are Doing Great! 🎉
                  </DialogTitle>
                  <DialogDescription className="text-sm sm:text-base md:text-lg text-gray-600 px-2">
                    <span className="block">You've completed all free levels!</span>
                    <span className="block font-bold text-lg sm:text-xl md:text-2xl text-[#4169E1] my-1">
                      {currentMemberCount} members earned!
                    </span>
                    <span className="block">What would you like to do next?</span>
                  </DialogDescription>
                </DialogHeader>
                
                {/* Success Icon */}
                <div className="flex justify-center py-4">
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-full p-6 sm:p-8">
                    <Trophy className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 text-[#4169E1]" />
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4 pb-2">
                  {/* Premium Upgrade Button */}
                  <Button
                    onClick={handlePremiumUpgrade}
                    className="w-full h-12 sm:h-14 md:h-16 text-sm sm:text-lg md:text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white rounded-xl sm:rounded-2xl shadow-lg transform hover:scale-105 transition-all"
                  >
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 flex-shrink-0" />
                    <span className="text-center leading-tight">
                      <span className="block sm:inline">Unlock Pro Levels 4–6 ($3)</span>
                      <span className="block sm:inline sm:ml-1 text-xs sm:text-sm opacity-90">• Earn 10x Members</span>
                    </span>
                  </Button>
                  
                  {/* Play Again Button */}
                  <Button
                    onClick={handleStartRound2}
                    variant="outline"
                    className="w-full h-12 sm:h-14 md:h-16 text-sm sm:text-lg md:text-xl font-bold border-2 border-[#4169E1] text-[#4169E1] hover:bg-[#4169E1] hover:text-white rounded-xl sm:rounded-2xl shadow-md transform hover:scale-105 transition-all"
                  >
                    Play Levels 1–3 Again
                  </Button>
                </div>
              </>
            )}

            {roundModalType === 'round2-win' && (
              <>
                <DialogHeader className="text-center">
                  <DialogTitle className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-green-600 mb-2">
                    You Win! 🏆
                  </DialogTitle>
                  <DialogDescription className="text-sm sm:text-base md:text-lg text-gray-600 px-2">
                    <span className="block">Congratulations! You reached</span>
                    <span className="block font-bold text-lg sm:text-xl md:text-2xl text-green-600 my-1">
                      {currentMemberCount} members!
                    </span>
                  </DialogDescription>
                </DialogHeader>
                
                {/* Win Icon */}
                <div className="flex justify-center py-4">
                  <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-full p-6 sm:p-8 animate-pulse">
                    <Crown className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 text-green-600" />
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4 pb-2">
                  {/* Premium Upgrade Button (only if not premium) */}
                  {(!user?.is_premium) && (
                    <Button
                      onClick={handlePremiumUpgrade}
                      className="w-full h-12 sm:h-14 md:h-16 text-sm sm:text-lg md:text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white rounded-xl sm:rounded-2xl shadow-lg transform hover:scale-105 transition-all"
                    >
                      <Crown className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 flex-shrink-0" />
                      <span className="text-center leading-tight">
                        <span className="block sm:inline">Unlock Pro Levels 4–6 ($3)</span>
                        <span className="block sm:inline sm:ml-1 text-xs sm:text-sm opacity-90">• Earn 10x Members</span>
                      </span>
                    </Button>
                  )}
                  
                  {/* Play Again Button */}
                  <Button
                    onClick={handleResetGame}
                    variant="outline"
                    className="w-full h-12 sm:h-14 md:h-16 text-sm sm:text-lg md:text-xl font-bold border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-xl sm:rounded-2xl shadow-md transform hover:scale-105 transition-all"
                  >
                    🎮 Play Again
                  </Button>
                </div>
              </>
            )}

            {roundModalType === 'round2-lose' && (
              <>
                <DialogHeader className="text-center">
                  <DialogTitle className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-red-600 mb-2">
                    You Lose 😞
                  </DialogTitle>
                  <DialogDescription className="text-sm sm:text-base md:text-lg text-gray-600 px-2">
                    <span className="block">You only reached</span>
                    <span className="block font-bold text-lg sm:text-xl md:text-2xl text-red-600 my-1">
                      {currentMemberCount} members
                    </span>
                    <span className="block text-xs sm:text-sm md:text-base opacity-75">
                      You needed 5000 members to win
                    </span>
                  </DialogDescription>
                </DialogHeader>
                
                {/* Lose Icon */}
                <div className="flex justify-center py-4">
                  <div className="bg-gradient-to-br from-red-100 to-red-200 rounded-full p-6 sm:p-8">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 flex items-center justify-center text-4xl sm:text-5xl md:text-6xl">
                      😞
                    </div>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4 pb-2">
                  {/* Premium Upgrade Button */}
                  <Button
                    onClick={handlePremiumUpgrade}
                    className="w-full h-12 sm:h-14 md:h-16 text-sm sm:text-lg md:text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white rounded-xl sm:rounded-2xl shadow-lg transform hover:scale-105 transition-all"
                  >
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 flex-shrink-0" />
                    <span className="text-center leading-tight">
                      <span className="block sm:inline">Unlock Pro Levels 4–6 ($3)</span>
                      <span className="block sm:inline sm:ml-1 text-xs sm:text-sm opacity-90">• Earn 10x Members</span>
                    </span>
                  </Button>
                  
                  {/* Reset Game Button */}
                  <Button
                    onClick={handleResetGame}
                    variant="outline"
                    className="w-full h-12 sm:h-14 md:h-16 text-sm sm:text-lg md:text-xl font-bold border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white rounded-xl sm:rounded-2xl shadow-md transform hover:scale-105 transition-all"
                  >
                    🔄 Reset Game
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
